import React, { useEffect, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import SearchBar from 'components/SearchBar';
import { usePaginatedClientsQuery } from 'queries/clients';
import InfiniteLoader from 'components/v2/InfiniteLoader';
import { useInView } from 'react-intersection-observer';
import { useUser } from 'common/context/UserContext';
import { useDebouncedValueNext } from 'common/hooks';
import { map, size } from 'lodash';
import { getDisplayName } from 'utils/client';
import { escapedRegexString } from 'utils/regex';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(),
    marginTop: theme.spacing(4),
  },
  list: {
    maxHeight: '20vh',
    overflowY: 'auto',
    paddingLeft: 6,
  },
  client: {
    padding: theme.spacing(),
    fontSize: '1.125rem',
    transition: theme.transitions.create(['background-color']),
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
      cursor: 'pointer',
    },
  },
  selected: {
    backgroundColor: '#e4e4e4',
    fontWeight: 'bold',
  },
  searchBar: {
    border: '1px solid #808080',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
}));

const noop = () => {};
const PAGE_SIZE = 20;

const ClientSelector = props => {
  const classes = useStyles();
  const { onSelect = noop, selectedClient, idKey = 'id', showCurrentClient = false } = props;
  const { setValue: setSearchTerm, debouncedValue: debouncedSearchTerm } = useDebouncedValueNext('', 150);
  const userContext = useUser();

  const clientId = userContext?.client?.id;
  const where = useMemo(() => {
    const where = {
      and: [
        {
          or: [{ archived: false }, { archived: { exists: false } }],
        },
        debouncedSearchTerm && {
          or: [
            { name: { regexp: escapedRegexString(debouncedSearchTerm) } },
            { fullName: { regexp: escapedRegexString(debouncedSearchTerm) } },
            { email: { regexp: escapedRegexString(debouncedSearchTerm) } },
          ],
        },
      ].filter(Boolean),
    };
    if (showCurrentClient && clientId) {
      where.id = {
        // neq wasn't working
        nin: [clientId],
      };
    }

    return where;
  }, [showCurrentClient, clientId, debouncedSearchTerm]);

  const { data, fetchNextPage, hasNextPage, isLoading, isError } = usePaginatedClientsQuery({
    pageSize: PAGE_SIZE,
    where,
  });

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  const clients = useMemo(() => {
    const clients = data ? data.pages.flatMap(page => page.clients).filter(c => c.id !== userContext?.client?.id) : [];
    if (showCurrentClient && userContext.client) {
      clients.unshift(userContext.client);
    }
    return clients;
  }, [data, userContext.client, showCurrentClient]);

  const clearSearch = () => setSearchTerm('');

  const handleSelect = client => {
    onSelect(client);
  };

  return (
    <div className={classes.root}>
      <SearchBar
        classes={{ root: classes.searchBar }}
        onChange={setSearchTerm}
        onCancelSearch={clearSearch}
        inputProps={{ 'aria-label': 'search for client' }}
        placeholder="Search by first or last name"
      />

      {/* ours */}
      <ul className={classes.list} data-test-id="search-results">
        {!isLoading && !isError && size(clients) === 0 && <div>No clients found</div>}
        {isLoading && <div>Loading...</div>}
        {isError && <div>Error loading clients</div>}
        {!isLoading &&
          size(clients) > 0 &&
          map(clients, client => (
            <li
              key={client.id}
              className={clsx(classes.client, selectedClient && selectedClient[idKey] === client[idKey] && classes.selected)}
              onClick={() => handleSelect(client)}
              style={{ listStyle: 'none' }}
              aria-label={`${client.name}`}
              data-test-id={selectedClient?.[idKey] === client[idKey] ? 'client-selected' : undefined}
            >
              {getDisplayName(client)}
            </li>
          ))}
        {hasNextPage && <InfiniteLoader ref={ref} text="Loading more clients" />}
      </ul>
    </div>
  );
};

export default ClientSelector;
